import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import SearchProvider from "./SearchProvider";
import { SearchBranchList } from "../../model/search/SearchBranchListModel";
import { useSelector } from "react-redux";
import { RootState } from "../../libraries/state_reducers/store";
import { textCase } from "../../service/ui/UI";
import { ProfileList } from "../../model/search/SearchProfileListModel";
import { updateSearchTeam } from "../../libraries/state_reducers/widget/DetailConnectDeptTeamReducer";
import { addModal } from "../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../const/ModalKeys";
import { useDispatch } from "react-redux";
import ChatLocationProvider, {
  MapLatLongType,
} from "../chats/chats/chat_location/ChatLocationProvider";
import SessionStorage from "../../service/storage/SessionStorage";
import StorageKeys from "../../const/StorageKeys";
import CacheStorage from "../../service/storage/CacheStorage";
import AppConfigService from "../../service/app_config/AppConfigService";
import { ResponsiveDeviceType } from "../../widget/layouts/ScreenMedia";
import { setDivisionDetail } from "../../libraries/state_reducers/divisions/DivisionsReducer";

type SearchContextType = {
  tabId: "1" | "2";
  searchValue: string;
  branchList: Array<SearchBranchList> | null | false;
  profileList: Array<ProfileList> | null | false;

  handleChangeTab: (tab: "1" | "2") => void;
  handleOnChange: (name: string, value: string) => void;
  clearSearch: () => void;

  onClickBranch: (branch: SearchBranchList) => void;
  onClickTeam: (team: ProfileList) => void;

  selectedBranch: SearchBranchList | null;

  countryId: string;
  countryName: string;
  stateId: string;
  stateName: string;
  cityId: string;
  cityName: string;
  areaId: string;
  areaName: string;

  latitude: number;
  longitude: number;

  nearByStatus: boolean;

  divisionTitle: string;
  switchDivision: () => void;

  companyGroupType: string;
};

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  const geoDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.geo
  );

  const geoRefreshSelector = useSelector(
    (state: RootState) => state.countryStateCityArea.timestamp
  );

  const divisionSelectedSelector = useSelector(
    (state: RootState) => state.divisions.divisionSelected
  );

  const companyGroupType: string =
    AppConfigService.get("company_group_type") ?? "0";

  const searchValueRef = useRef<string>("");
  const searchAbortControllerRef = useRef<AbortController>();
  const branchListResultRef = useRef<Array<SearchBranchList> | null>(null);
  const branchDetailtRef = useRef<SearchBranchList | null>(null);

  const [tabId, setTabId] = useState<"1" | "2">("1");
  const [searchValue, setSearchValue] = useState<string>("");

  const [branchList, setBranchList] = useState<
    Array<SearchBranchList> | null | false
  >(null);
  const [profileList, setProfileList] = useState<
    Array<ProfileList> | null | false
  >(null);

  const [selectedBranch, setSelectedBranch] = useState<SearchBranchList | null>(
    null
  );

  const [divisionTitle, setDivisionTitle] = useState<string>("");

  const [countryId, setCountryIdField] = useState<string>("0");
  const [countryName, setCountryNameField] = useState<string>("");
  const [stateId, setStateIdField] = useState<string>("0");
  const [stateName, setStateNameField] = useState<string>("");
  const [cityId, setCityIdField] = useState<string>("0");
  const [cityName, setCityNameField] = useState<string>("");
  const [areaId, setAreaIdField] = useState<string>("0");
  const [areaName, setAreaNameField] = useState<string>("");

  const [latitude, setLatitude] = useState<number>(0);
  const [longitude, setLongitude] = useState<number>(0);

  const [nearByStatus, setNearByStatus] = useState<boolean>(false);

  const handleChangeTab = (tab: "1" | "2") => {
    setTabId(tab);
    clearSearch();
  };

  const handleOnChange = async (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    setSearchValue(fieldValue);
    searchValueRef.current = fieldValue;

    if (tabId === "1") {
      if (fieldName === `search-branch`) {
        const filtered: Array<SearchBranchList> | undefined =
          branchListResultRef.current?.filter((branch) =>
            Object.values(branch)
              .join(" ")
              .toLowerCase()
              .includes(searchValueRef.current.toLowerCase())
          );

        setBranchList(filtered!);
      }
    } else {
      if (fieldValue.length > 2) {
        searchProfilePAPI("", fieldValue);
      }
    }
  };

  const clearSearch = () => {
    setSearchValue("");
    setProfileList(false);
    setBranchList(branchListResultRef.current);
    searchValueRef.current = "";
  };

  const getBranchListAPI = async (params: {
    cId?: string;
    sId?: string;
    citId?: string;
    aId?: string;
    coodinates?: { lat: number; lng: number };
  }) => {
    setBranchList(null);

    let platitude: string = latitude > 0 ? latitude.toString() : "";
    let plongitude: string = longitude > 0 ? longitude.toString() : "";

    if (params.coodinates !== undefined) {
      platitude =
        params.coodinates.lat > 0 ? params.coodinates.lat.toString() : "";
      plongitude =
        params.coodinates.lng > 0 ? params.coodinates.lng.toString() : "";
    }

    const branchListResult: Array<SearchBranchList> | null =
      await SearchProvider.getBranchListAPI({
        country_id: params.cId || countryId,
        state_id: params.sId || stateId,
        city_id: params.citId || cityId,
        area_id: params.aId || areaId,
        latitude: platitude,
        longitude: plongitude,
      });

    setProfileList(branchListResult == null ? false : branchListResult);
    setBranchList(branchListResult);
    branchListResultRef.current = branchListResult;
  };

  const searchProfilePAPI = async (bId: string, keyword: string = "") => {
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    searchAbortControllerRef.current = new AbortController();
    const signal = searchAbortControllerRef.current.signal;

    setProfileList(null);

    const profileListResult: Array<ProfileList> | null =
      await SearchProvider.getProfileListAPI({
        branchId: bId,
        keyword: keyword,
        signal,
      });

    setProfileList(profileListResult);
  };

  const onClickBranch = async (branch: SearchBranchList) => {
    setTabId("2");
    setSelectedBranch(branch);
    searchProfilePAPI(branch?.branch_id!, "");
    branchDetailtRef.current = branch;
  };

  const applyLocationFilter = async () => {
    if (geoDispatch != null) {
      let params = {
        cId: "0",
        sId: "0",
        citId: "0",
        aId: "0",
        coodinates: { lat: 0, lng: 0 },
      };
      if (geoDispatch?.country !== null) {
        const cId: string = geoDispatch?.country?.country_id! ?? "0";
        params = { ...params, cId: cId };

        setCountryIdField(cId);
        setCountryNameField(
          textCase(geoDispatch?.country?.country_name! ?? "")
        );
      }
      if (geoDispatch?.state !== null) {
        const sId: string = geoDispatch?.state?.state_id! ?? "0";
        params = { ...params, sId: sId };

        setStateIdField(sId);
        setStateNameField(textCase(geoDispatch?.state?.state_name! ?? ""));
      }
      if (geoDispatch?.city !== null) {
        const citId: string = geoDispatch?.city?.city_id! ?? "0";
        params = { ...params, citId: citId };

        setCityIdField(citId);
        setCityNameField(textCase(geoDispatch?.city?.city_name! ?? ""));
      }
      if (geoDispatch?.area !== null) {
        const aId: string = geoDispatch?.area?.area_id! ?? "0";
        params = { ...params, aId: aId };

        setAreaIdField(aId);
        setAreaNameField(textCase(geoDispatch?.area?.area_name! ?? ""));
      }

      const nearByStatus: boolean = geoDispatch?.nearBy ?? false;
      setNearByStatus(nearByStatus);

      // GET NEAREST
      if (nearByStatus) {
        const coordinates: MapLatLongType | null =
          await ChatLocationProvider.getGoogleCurrentLocation();

        console.log(coordinates);
        let cLat: number = 0,
          cLng: number = 0;
        if (coordinates != null) {
          cLat = coordinates.lat;
          cLng = coordinates.lng;

          params = {
            ...params,
            coodinates: { lat: cLat, lng: cLng },
          };
        }
        setLatitude(cLat);
        setLongitude(cLng);
      }

      getBranchListAPI(params);
    }
  };

  const onClickTeam = (detail: ProfileList) => {
    dispatch(updateSearchTeam(detail));
    dispatch(
      addModal({
        id: ModalKeys.connect_detail_branch_team,
        title: detail?.name!,
        size: "md",
        allowBottomSheet: true,
        hideHeader: true,
        bottomSheetHideTitle: true,
      })
    );
  };

  const resetForm = () => {
    setCountryIdField("0");
    setCountryNameField("");

    setStateIdField("0");
    setStateNameField("");

    setCityIdField("0");
    setCityNameField("");

    setAreaIdField("0");
    setAreaNameField("");

    setNearByStatus(false);

    setDivisionTitle(CacheStorage.getValue(StorageKeys.division_title));

    if (companyGroupType === "1") {
      SessionStorage.saveValue(
        StorageKeys.division_id,
        CacheStorage.getValue(StorageKeys.division_id) || ""
      );
    }
  };

  const switchDivision = () => {
    if (companyGroupType === "1") {
      dispatch(
        addModal({
          id: ModalKeys.list_choose_division,
          title: type === 3 ? "choose_division" : "divisions",
          screenMobileView: type !== 3,
        })
      );
    }
  };

  useEffect(() => {
    resetForm();
    getBranchListAPI({});
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    applyLocationFilter();
    // eslint-disable-next-line
  }, [geoRefreshSelector]);

  useEffect(() => {
    if (divisionSelectedSelector != null) {
      SessionStorage.saveValue(
        StorageKeys.division_id,
        divisionSelectedSelector.division_id!
      );
      dispatch(setDivisionDetail(null));

      setDivisionTitle(divisionSelectedSelector.title!);
      getBranchListAPI({
        cId: countryId,
        sId: stateId,
        citId: cityId,
        aId: areaId,
      });
    }
    // eslint-disable-next-line
  }, [divisionSelectedSelector]);

  return (
    <SearchContext.Provider
      value={{
        tabId,
        handleChangeTab,

        searchValue,
        handleOnChange,
        clearSearch,

        branchList,
        profileList,

        countryId,
        countryName,
        stateId,
        stateName,
        cityId,
        cityName,
        areaId,
        areaName,

        latitude,
        longitude,

        onClickBranch,
        selectedBranch,

        onClickTeam,

        nearByStatus,

        divisionTitle,
        switchDivision,

        companyGroupType,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const SearchContextImport = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error(
      "SearchContextImport must be used within a SearchContextProvider"
    );
  }
  return context;
};
